import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductDetail } from '@/lib/api';
import { Swatch } from './Swatch';
import { SideChoice } from './SideChoice';
import type { JSX } from 'react';

interface Props {
  options: ProductDetail['options'];
  variants: ProductDetail['variants'];
  selection: Record<string, string>;
  onSelect: (axis: string, value: string) => void;
}

/**
 * Progressive selection across up to 5 axes and 59 variants.
 *
 * Each choice narrows the next, and an unreachable combination is **visibly disabled**
 * rather than silently missing — `Łóżko ramowe BORGO` has 120 possible combinations across
 * its four axes and only 59 real ones, so a naive selector dead-ends more often than not.
 */
/** Whether a tick on this swatch needs to be white. Rec. 601 luma on the dominant hex. */
function onDark(hex: string[]): boolean {
  const first = hex[0];
  if (!first || !/^#[0-9a-f]{6}$/i.test(first)) return false;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(first.slice(i, i + 2), 16));
  return 0.299 * (r ?? 0) + 0.587 * (g ?? 0) + 0.114 * (b ?? 0) < 140;
}

export function VariantSelector({ options, variants, selection, onSelect }: Props): JSX.Element {
  const { t } = useTranslation();

  /**
   * For each axis, which values still lead to a real variant given everything chosen on
   * the *other* axes. Computed against the full variant list, so it is always accurate.
   */
  const reachable = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const option of options) {
      const others = options.filter((o) => o.axisSlug !== option.axisSlug).map((o) => o.axisSlug);
      const allowed = new Set<string>();
      for (const variant of variants) {
        const compatible = others.every((axis) => !selection[axis] || variant.optionValues[axis] === selection[axis]);
        const value = variant.optionValues[option.axisSlug];
        if (compatible && value) allowed.add(value);
      }
      map.set(option.axisSlug, allowed);
    }
    return map;
  }, [options, variants, selection]);

  const allChosen = options.every((o) => selection[o.axisSlug]);
  const matched = allChosen
    ? variants.find((v) => options.every((o) => v.optionValues[o.axisSlug] === selection[o.axisSlug]))
    : undefined;

  return (
    <div className="space-y-6" data-testid="variant-selector">
      {/* Screen readers are told when a selection changes what is available. */}
      <p aria-live="polite" className="sr-only">
        {allChosen
          ? matched
            ? `${t('product.addToCart')} — ${matched.sku}`
            : t('product.unavailableCombination')
          : t('product.selectOptions')}
      </p>

      {options.map((option) => {
        const allowed = reachable.get(option.axisSlug) ?? new Set<string>();
        const disabledValues = new Set(
          option.values.filter((v) => !allowed.has(v.slug)).map((v) => v.slug),
        );
        const chosen = selection[option.axisSlug] ?? null;

        return (
          <fieldset key={option.axisSlug} className="space-y-2.5">
            <legend className="flex w-full items-baseline justify-between gap-2 text-xs font-semibold mb-1">
              <span>{option.label}</span>
              {chosen && (
                <span className="font-normal text-[var(--color-muted)]">
                  {option.values.find((v) => v.slug === chosen)?.label}
                </span>
              )}
            </legend>

            {option.widget === 'side' ? (
              <SideChoice
                values={option.values}
                selected={chosen}
                disabledValues={disabledValues}
                onSelect={(value) => onSelect(option.axisSlug, value)}
              />
            ) : option.widget === 'swatch' ? (
              <div role="radiogroup" aria-label={option.label} className="flex flex-wrap gap-2">
                {option.values.map((value) => {
                  const disabled = disabledValues.has(value.slug);
                  return (
                    <button
                      key={value.slug}
                      type="button"
                      role="radio"
                      aria-checked={chosen === value.slug}
                      aria-label={value.label}
                      aria-disabled={disabled}
                      disabled={disabled}
                      title={disabled ? `${value.label} — ${t('product.unavailableCombination')}` : value.label}
                      onClick={() => onSelect(option.axisSlug, value.slug)}
                      className="relative shrink-0"
                      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                      data-disabled={disabled || undefined}
                    >
                      <Swatch hex={value.hex} label={value.label} size={40} selected={chosen === value.slug} disabled={disabled} />
                      {/* A ring alone asks the eye to compare two swatches to find the
                          chosen one. A tick states it. Its colour is picked from the
                          swatch's own luminance so it stays legible on BIAŁY and on
                          CZARNY alike. */}
                      {chosen === value.slug && !disabled && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 flex items-center justify-center"
                        >
                          <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4"
                               style={{ color: onDark(value.hex) ? '#fff' : 'var(--color-ink)' }}>
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3"
                                  strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      )}
                      {/* Unavailable, not broken: a muted rule rather than a red one, which
                          reads as an error the customer has caused. */}
                      {disabled && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 flex items-center justify-center"
                        >
                          <span className="block h-[1.5px] w-9 rotate-45 rounded-full bg-[var(--color-muted)]" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div role="radiogroup" aria-label={option.label} className="flex flex-wrap gap-1.5">
                {option.values.map((value) => {
                  const disabled = disabledValues.has(value.slug);
                  const active = chosen === value.slug;
                  return (
                    <button
                      key={value.slug}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      aria-disabled={disabled}
                      disabled={disabled}
                      onClick={() => onSelect(option.axisSlug, value.slug)}
                      data-disabled={disabled || undefined}
                      className="px-3 py-1.5 text-xs font-medium transition-colors duration-200"
                      style={{
                        background: active ? 'var(--color-ink)' : 'var(--color-ground-lift)',
                        color: active ? 'var(--color-ground)' : disabled ? 'var(--color-muted)' : 'var(--color-ink)',
                        boxShadow: active ? undefined : 'inset 0 0 0 1px var(--color-line)',
                        opacity: disabled ? 0.4 : 1,
                        textDecoration: disabled ? 'line-through' : undefined,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                      title={disabled ? `${value.label} — ${t('product.unavailableCombination')}` : undefined}
                    >
                      {value.label}
                      {/* A value cut off by the supplier's 150-char title limit is marked. */}
                      {value.truncated && <span className="ml-1 opacity-50" title="…">…</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </fieldset>
        );
      })}

      {allChosen && !matched && (
        <p role="alert" className="text-xs text-[var(--color-danger)]">
          {t('product.unavailableCombination')}
        </p>
      )}
    </div>
  );
}
